/**
 * The two ways an auditor reads one audit.
 *
 * `auditor.getControlsTree` returns the framework's REQUIREMENT spine — one row
 * per criterion (CC6.1) with the tests whose evidence cites it — plus, since
 * the control layer landed, one row per Screenata CONTROL (GOV-01 "Board
 * Oversight") with the tests that operate it. Same tests, two groupings.
 *
 * This module turns those two arrays into the indexes both views read. It is
 * deliberately pure: the tree, the requirement pane and the control pane must
 * agree on what "in scope", "accepted" and "reviewed" mean, and a counter that
 * disagrees with the list under it is how an auditor stops trusting the page.
 */

import { getAuditorControlStatus } from '@deepguide-ai/types'
import { isTestDeactivated } from './auditor-test-status'
import type { AuditorControlReviewDTO, ControlApprovalDTO } from '@deepguide-ai/types'

/** Where a test's evidence stands, in the auditor's terms. */
export type EvidenceState =
  | 'accepted'
  | 'submitted'
  | 'changes'
  | 'none'
  | 'deferred'

export const EVIDENCE_STATE_LABEL: Record<EvidenceState, string> = {
  accepted: 'Submitted',
  submitted: 'Submitted',
  changes: 'Changes requested',
  none: 'No evidence yet',
  deferred: 'Deferred',
}

/** Verdict labels. `REVIEWED` was withdrawn as a verdict — nothing writes it
 *  anywhere any more — but the rows recorded before that still render, so the
 *  label stays. */
export const VERDICT_LABEL: Record<string, string> = {
  SATISFACTORY: 'Satisfactory',
  EXCEPTION_NOTED: 'Exception noted',
  NEEDS_REMEDIATION: 'Needs remediation',
  REVIEWED: 'Reviewed',
}

export interface ReviewTest {
  statusId: string
  taskCode?: string | null
  title?: string | null
  status?: string | null
  scopeState?: string | null
  deferralKind?: string | null
  deactivationReason?: string | null
  reviewStatus?: string | null
  hasSubmittedEvidence?: boolean
  hasApprovedSubmission?: boolean
  submissionStatus?: string | null
  lastSubmittedAt?: string | Date | null
  linkedEvidenceCount?: number
  commentCount?: number
  unresolvedRequestCount?: number
  coveredDays?: number | null
  coverageGapDays?: number | null
  /** How the test is collected. Not rendered — it gates the run-history panel
   *  in `TestDetailPanel`, because an automated check's evidence is its runs. */
  collection?: string | null
  /** A terminal automated observation is evidence even when its verdict fails. */
  latestApiRunHasEvidence?: boolean
  /** Current automated health, separate from evidence availability. */
  latestApiRunStatus?: string | null
  /** RFC-193 — how far the covering checks have got against their anchor. */
  anchorState?: string | null
  /** Set when this test is an automated check covering another test. */
  coversAnchorId?: string | null
}

/** A Screenata control, as an auditor sees it: a display code, never `SC-*`. */
export interface ReviewControl {
  id: string
  code: string
  title: string
  description?: string | null
  domain: string
  domainOrder: number
  citations: string[]
  testStatusIds: string[]
  approvalStage?: ControlApprovalDTO['stage']
  auditorReview?: AuditorControlReviewDTO | null
}

/** A framework requirement (CC6.1) — the spine row the server already built. */
export interface ReviewRequirement {
  id: string
  /** The requirement ref itself (`CC6.1`), which is what an auditor reads. */
  ref: string
  shortTitle: string
  /** The criterion text, verbatim. */
  criterion: string | null
  category: string
  tests: ReviewTest[]
  implementingControlIds: string[]
  effectiveAuditStatus?: string | null
  /** The company scoped this criterion out — a different statement from "no
   *  evidence yet", and the one the header's out-of-scope count is made of. */
  isNotApplicable?: boolean
  unresolvedRequestCount?: number
  /** What the company said about this requirement — the scope decision, the
   *  note on the control, and the responsibilities it hands the customer.
   *  Carried through because the pane replaces the header that showed them. */
  scopeReason?: string | null
  companyNotes?: string | null
  companyResponsibilities?: string | null
}

export interface ReviewModel {
  requirements: ReviewRequirement[]
  controls: ReviewControl[]
  testsById: Map<string, ReviewTest>
  /** Tests left out because they are deferred, and the controls that had
   *  nothing else — what the "show deferred" affordance offers to bring back.
   *  Zero when the model was built with them included. */
  deferredCount: number
  deferredControlCount: number
  /** Requirements a control cites, resolved to rows present in this cycle. */
  requirementsByControlId: Map<string, ReviewRequirement[]>
  /** Controls mapped to a requirement, in reading order. */
  controlsByRequirementRef: Map<string, ReviewControl[]>
}

interface TreePage {
  groups?: Array<{ framework: string; controls: any[] }>
  controlLayer?: ReviewControl[]
  controlOnlyTests?: ReviewTest[]
}

/**
 * Where a test's evidence stands. Order matters: a scoped-out test is deferred
 * whatever its evidence says, and an approved submission outranks a later
 * revision still in review. This legacy Test approval displays as Submitted;
 * company approval now belongs to the Control, and auditor verdicts are separate.
 */
export function evidenceStateOf(test: ReviewTest): EvidenceState {
  if (isTestDeactivated(test)) return 'deferred'
  if (test.hasApprovedSubmission) return 'accepted'
  if (test.submissionStatus === 'NEEDS_REVISION') return 'changes'
  if (test.submissionStatus === 'PENDING' || test.lastSubmittedAt) return 'submitted'
  // An automated check never submits: its evidence IS the run history the test
  // panel shows. Reading a check that ran as "No evidence yet" put every
  // automated control in the coverage-gap list.
  if (test.latestApiRunStatus) return 'submitted'
  return 'none'
}

export interface ReviewSummary {
  total: number
  /** Everything the engagement actually asks for — deferred tests excluded. */
  active: number
  accepted: number
  submitted: number
  changes: number
  none: number
  deferred: number
  /** In-scope tests carrying an auditor verdict. */
  reviewed: number
}

export function summarize(tests: readonly ReviewTest[]): ReviewSummary {
  const s: ReviewSummary = {
    total: tests.length, active: 0, accepted: 0, submitted: 0,
    changes: 0, none: 0, deferred: 0, reviewed: 0,
  }
  for (const test of tests) {
    s[evidenceStateOf(test)]++
    if (evidenceStateOf(test) === 'deferred') continue
    s.active++
    if (test.reviewStatus && test.reviewStatus !== 'REVIEWED') s.reviewed++
  }
  return s
}

/** Evidence coverage counts every applicable Test, including covering checks.
 * A failing observation is evidence; passing health is a separate counter. */
export function summarizeAuditEvidence(tests: readonly ReviewTest[]) {
  const summary = { total: 0, withEvidence: 0, manual: 0, submitted: 0, automated: 0, passing: 0 }
  for (const test of dedupeTests(tests)) {
    if (isTestDeactivated(test)) continue
    summary.total++
    if (test.collection === 'automated') {
      summary.automated++
      if (test.latestApiRunHasEvidence) {
        summary.withEvidence++
        if (test.latestApiRunStatus === 'PASS') summary.passing++
      }
    } else {
      summary.manual++
      if (test.hasSubmittedEvidence) {
        summary.submitted++
        summary.withEvidence++
      }
    }
  }
  return summary
}

/** Controls use the handoff stage; requirement dots summarize Test evidence. */
export function toneOf(s: ReviewSummary, control?: Parameters<typeof getAuditorControlStatus>[0]): 'green' | 'blue' | 'amber' | 'red' | 'gray' | 'hollow' {
  if (control) {
    const status = getAuditorControlStatus(control)
    if (status === 'OUT_OF_SCOPE') return 'hollow'
    if (status === 'PENDING_SUBMISSION' || status === 'UNAVAILABLE') return 'gray'
    if (status === 'SATISFACTORY') return 'green'
    return status === 'EXCEPTION_NOTED' ? 'red' : 'amber'
  }
  if (s.active === 0) return 'hollow'
  if (s.changes > 0) return 'amber'
  if (s.none > 0) return 'gray'
  if (s.submitted > 0) return 'blue'
  return 'green'
}

/** An automated check that covers another test is reviewed through its anchor,
 *  so it is never a row of its own in these views. `present` is the whole
 *  cycle's index: a check whose anchor is linked to a DIFFERENT control is
 *  still reviewed through that anchor, and listing it twice would offer two
 *  review affordances for one CES. A check whose anchor is not in the cycle at
 *  all stays a first-class row — otherwise it would vanish. */
export function isCoveringCheck(test: ReviewTest, present: ReadonlySet<string> | Map<string, unknown>): boolean {
  return Boolean(test.coversAnchorId && present.has(test.coversAnchorId))
}

/** Cycle-scoped monitoring sources, including checks linked through another Control. */
export const coveringChecksByAnchor = (model: ReviewModel): Map<string, ReviewTest[]> => {
  const byAnchor = new Map<string, ReviewTest[]>()
  for (const test of model.testsById.values()) {
    const anchor = test.coversAnchorId
    if (!anchor || !model.testsById.has(anchor)) continue
    byAnchor.set(anchor, [...(byAnchor.get(anchor) ?? []), test])
  }
  return byAnchor
}

const dedupeTests = (tests: readonly ReviewTest[]): ReviewTest[] => {
  const byId = new Map<string, ReviewTest>()
  for (const test of tests) if (!byId.has(test.statusId)) byId.set(test.statusId, test)
  return [...byId.values()]
}

/**
 * Build both indexes from the pages react-query holds.
 *
 * The control layer is identical on every page (the server paginates only the
 * requirement spine), so the last page wins; requirement rows accumulate.
 *
 * `includeDeferred: false` leaves deferred tests out of every index, so the
 * counts, the tone dots, the tables and the Prev/Next walk all describe the
 * same, smaller audit — an auditor's own list is a list of work they have to
 * do, and a deferred test is by definition not that. The model still reports
 * how many were left out so the view can offer them back.
 */
export function buildReviewModel(
  pages: readonly TreePage[],
  options: { includeDeferred?: boolean } = {},
): ReviewModel {
  const includeDeferred = options.includeDeferred ?? true
  const requirements: ReviewRequirement[] = []
  const testsById = new Map<string, ReviewTest>()
  const deferredTestIds = new Set<string>()
  const keep = (test: ReviewTest): boolean => {
    if (includeDeferred || evidenceStateOf(test) !== 'deferred') return true
    // A covering check is not an ask — it is provenance for an anchor that is
    // in scope, and the anchor's "N automated checks" has to stay truthful.
    // Filtering one out would quietly change what stands behind the evidence.
    if (test.coversAnchorId) return true
    deferredTestIds.add(test.statusId)
    return false
  }

  for (const page of pages) {
    for (const group of page.groups ?? []) {
      for (const row of group.controls) {
        const tests: ReviewTest[] = (row.tests ?? []).filter(keep)
        for (const test of tests) if (!testsById.has(test.statusId)) testsById.set(test.statusId, test)
        requirements.push({
          id: row.id,
          ref: row.controlId,
          shortTitle: row.title ?? row.controlId,
          criterion: row.description ?? null,
          // The framework's own grouping when it has one; otherwise the row's
          // own group, which is how auditor-requested tests keep their
          // "Auditor requests" heading instead of being filed under a
          // criterion category they do not have.
          category: row.requirementCategory ?? row.framework ?? group.framework ?? 'Requirements',
          tests,
          implementingControlIds: row.implementingControlIds ?? [],
          effectiveAuditStatus: row.effectiveAuditStatus ?? null,
          isNotApplicable: Boolean(row.isNotApplicable),
          unresolvedRequestCount: row.unresolvedRequestCount ?? 0,
          scopeReason: row.scopeReason ?? null,
          companyNotes: row.companyNotes ?? null,
          companyResponsibilities: row.companyResponsibilities ?? null,
        })
      }
    }
    for (const test of page.controlOnlyTests ?? []) {
      if (!keep(test)) continue
      if (!testsById.has(test.statusId)) testsById.set(test.statusId, test)
    }
  }

  const allControls = pages.reduce<ReviewControl[]>(
    (acc, page) => (page.controlLayer?.length ? page.controlLayer : acc),
    [],
  )
  // A control stays only when it lists a live test: one the engagement asks for
  // that is a row of its own. A deferred covering check is kept in the index
  // (its anchor's "N automated checks" must stay true) but is not a row, so a
  // control holding only deferred tests and covering checks read "0 tests" —
  // AI-04 on WOX's SOC 2 cycle, alone under its domain heading. This is the
  // customer's SOC 2 Controls view: it lists exactly the controls with an
  // active test. One that never had a test stays: "mapped here, no evidence"
  // is a coverage fact, not a hidden row.
  const isLiveRow = (id: string): boolean => {
    const test = testsById.get(id)
    return Boolean(test && evidenceStateOf(test) !== 'deferred' && !isCoveringCheck(test, testsById))
  }
  const controls = includeDeferred
    ? allControls
    : allControls.filter(
        (control) => control.testStatusIds.length === 0 || control.testStatusIds.some(isLiveRow),
      )

  const requirementByRef = new Map(requirements.map((r) => [r.ref, r]))
  const requirementsByControlId = new Map<string, ReviewRequirement[]>()
  const controlsByRequirementRef = new Map<string, ReviewControl[]>()
  for (const control of controls) {
    const cited = control.citations
      .map((ref) => requirementByRef.get(ref))
      .filter((r): r is ReviewRequirement => Boolean(r))
    requirementsByControlId.set(control.id, cited)
    for (const requirement of cited) {
      controlsByRequirementRef.set(requirement.ref, [
        ...(controlsByRequirementRef.get(requirement.ref) ?? []),
        control,
      ])
    }
  }

  return {
    requirements,
    controls,
    testsById,
    requirementsByControlId,
    controlsByRequirementRef,
    deferredCount: deferredTestIds.size,
    deferredControlCount: allControls.length - controls.length,
  }
}

/** Unique Controls per framework category; a Control may belong to several categories. */
export function controlsByAuditCategory(model: ReviewModel, framework: string): Array<{
  category: string
  controls: ReviewControl[]
}> {
  const groups = new Map<string, Map<string, ReviewControl>>()
  for (const requirement of model.requirements) {
    if (requirement.isNotApplicable || requirement.category === 'Auditor requests') continue
    const category = framework === 'SOC2'
      ? requirement.category.startsWith('CC')
        ? 'Security'
        : requirement.category.split(' - ').slice(1).join(' - ') || requirement.category
      : requirement.category
    const controls = groups.get(category) ?? new Map<string, ReviewControl>()
    for (const control of model.controlsByRequirementRef.get(requirement.ref) ?? []) {
      controls.set(control.id, control)
    }
    groups.set(category, controls)
  }
  return Array.from(groups, ([category, controls]) => ({ category, controls: [...controls.values()] }))
}

/** Deferred tests sort last wherever they are listed: they are the engagement's
 *  footnote, and an auditor scanning a control reads the live asks first. */
const deferredLast = (tests: readonly ReviewTest[]): ReviewTest[] =>
  [...tests].sort(
    (a, b) => Number(evidenceStateOf(a) === 'deferred') - Number(evidenceStateOf(b) === 'deferred'),
  )

/** A control's tests, resolved and deduped, anchors before covering checks. */
export function testsOfControl(model: ReviewModel, control: ReviewControl): ReviewTest[] {
  const tests = dedupeTests(
    control.testStatusIds
      .map((id) => model.testsById.get(id))
      .filter((t): t is ReviewTest => Boolean(t)),
  )
  return deferredLast(tests.filter((test) => !isCoveringCheck(test, model.testsById)))
}

/** Every test under a requirement, by whichever control operates it. */
export function testsOfRequirement(model: ReviewModel, requirement: ReviewRequirement): ReviewTest[] {
  const tests = dedupeTests(requirement.tests)
  return deferredLast(tests.filter((test) => !isCoveringCheck(test, model.testsById)))
}

/**
 * Keep explicitly mapped controls and add the controls operating this
 * requirement's tests. A control contributes only its tests citing this
 * requirement; its other tests must not broaden the criterion's evidence.
 */
export function requirementControlSections(
  model: ReviewModel,
  requirement: ReviewRequirement,
): { control: ReviewControl; tests: ReviewTest[] }[] {
  const testIds = new Set(testsOfRequirement(model, requirement).map((test) => test.statusId))
  const mapped = model.controlsByRequirementRef.get(requirement.ref) ?? []
  const mappedIds = new Set(mapped.map((control) => control.id))
  const controls = [
    ...mapped,
    ...model.controls.filter((control) =>
      !mappedIds.has(control.id) && control.testStatusIds.some((id) => testIds.has(id)),
    ),
  ]
  return controls.map((control) => ({
    control,
    tests: testsOfControl(model, control).filter((test) =>
      testIds.has(test.statusId),
    ),
  }))
}

export function unplacedRequirementTests(
  model: ReviewModel,
  requirement: ReviewRequirement,
): ReviewTest[] {
  const claimed = new Set(
    requirementControlSections(model, requirement).flatMap((section) =>
      section.tests.map((test) => test.statusId),
    ),
  )
  return testsOfRequirement(model, requirement).filter((test) => !claimed.has(test.statusId))
}

/** #1425 — the Type II period-coverage gap over a set of tests. The worst gap,
 *  never the sum: two tests' uncovered windows are not the same days. */
export function coverageGapOf(tests: readonly ReviewTest[]): { count: number; worst: number | null } {
  const gaps = tests
    .map((test) => test.coverageGapDays ?? 0)
    .filter((days) => days > 0)
  return { count: gaps.length, worst: gaps.length ? Math.max(...gaps) : null }
}

/** Case-insensitive contains, over whatever the caller considers searchable. */
export function matchesSearch(search: string, ...parts: Array<string | null | undefined>): boolean {
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return parts.filter(Boolean).join(' ').toLowerCase().includes(needle)
}
